import React, { useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  IconButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { FaEye, FaEyeSlash, FaLock } from "react-icons/fa";
import BackgroundImage from "../images/map4.jpg";
import Footer from "./Footer";

const Register = () => {
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
  });

  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${process.env.REACT_APP_URL}/register`, formData, {
        withCredentials: true,
      });
      setSuccess("Surveyor registered successfully");
      setError("");
      setTimeout(() => navigate("/login"), 2000);
    } catch (error) {
      setError(error.response?.data?.message || "Failed to register user");
      setSuccess("");
      console.error("Error registering:", error);
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "100vh",
          height: "auto",
          backgroundImage: `url(${BackgroundImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
          padding: isMobile ? 2 : 4,
        }}
      >
        <Paper
          elevation={6}
          sx={{
            p: isMobile ? 3 : 4,
            width: "100%",
            maxWidth: 380,
            textAlign: "center",
            borderRadius: 3,
            bgcolor: "rgba(255, 255, 255, 0.95)",
            margin: "auto",
          }}
        >
          {/* Logo */}
          <img
            src="/images/logobaru.png"
            alt="Logo Aplikasi"
            style={{ 
              width: isMobile ? "60px" : "80px", 
              marginBottom: isMobile ? "8px" : "10px" 
            }}
          />
          <Typography 
            variant={isMobile ? "subtitle1" : "h6"} 
            gutterBottom
            sx={{ fontSize: isMobile ? "0.9rem" : "1.25rem" }}
          >
            Sistem Informasi Digital Layanan dan Manajemen Perumahan
          </Typography>

          {/* Status Messages */}
          {error && (
            <Typography color="error" fontSize={isMobile ? "0.8rem" : "1rem"}>
              {error}
            </Typography>
          )}
          {success && (
            <Typography color="success" fontSize={isMobile ? "0.8rem" : "1rem"}>
              {success}
            </Typography>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Username"
              margin="normal"
              variant="filled"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              size={isMobile ? "small" : "medium"}
              sx={{
                "& .MuiFilledInput-root": {
                  backgroundColor: "white",
                  borderRadius: "8px",
                },
                "& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after": {
                  display: "none",
                },
                "& .MuiInputLabel-root": {
                  fontSize: isMobile ? "0.8rem" : "1rem",
                },
                mt: isMobile ? 1 : 2,
              }}
            />

            <TextField
              fullWidth
              label="Email"
              margin="normal"
              variant="filled"
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              size={isMobile ? "small" : "medium"}
              sx={{
                "& .MuiFilledInput-root": {
                  backgroundColor: "white",
                  borderRadius: "8px",
                },
                "& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after": {
                  display: "none",
                },
                "& .MuiInputLabel-root": {
                  fontSize: isMobile ? "0.8rem" : "1rem",
                },
                mt: isMobile ? 1 : 2,
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? "text" : "password"}
              margin="normal"
              variant="filled"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              size={isMobile ? "small" : "medium"}
              sx={{
                "& .MuiFilledInput-root": {
                  backgroundColor: "white",
                  borderRadius: "8px",
                },
                "& .MuiFilledInput-underline:before, & .MuiFilledInput-underline:after": {
                  display: "none",
                },
                "& .MuiInputLabel-root": {
                  fontSize: isMobile ? "0.8rem" : "1rem",
                },
                mt: isMobile ? 1 : 2,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      size="small"
                    >
                      {showPassword ? <FaEyeSlash size={isMobile ? 16 : 18} /> : <FaEye size={isMobile ? 16 : 18} />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Tombol Register */}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              size={isMobile ? "medium" : "large"}
              sx={{
                mt: 2,
                display: "flex",
                alignItems: "center",
                gap: 1,
                py: isMobile ? 1 : 1.5,
              }}
            >
              <FaLock size={isMobile ? 12 : 14} /> Register
            </Button>
            
            {/* Login Link */}
            <Button
              fullWidth
              variant="text"
              color="primary"
              onClick={() => navigate("/login")}
              size={isMobile ? "small" : "medium"}
              sx={{ 
                mt: 1, 
                textTransform: "none",
                fontSize: isMobile ? "0.75rem" : "0.875rem"
              }}
            >
              Already have an account? Login here
            </Button>
          </form>
        </Paper>
      </Box>
      <Footer />
    </>
  );
};

export default Register;